/// <amd-module name="Repos/Views/PushDetails/Components/PushDetailsView" />
import * as React from "react";
import { ChangesExplorerView } from "Repos/Changes/Components/ChangesExplorerView";
import { ChangesContext } from "Repos/ChangesCommon/ChangesContext";
import { getGitNavigationService } from "Repos/ChangesCommon/ChangesNavigationService";
import { IChange, IChangeCollection } from "Repos/ChangesCommon/Contracts";
import { CommitsHistoryView } from "Repos/CommitsList/Components/CommitsHistoryView";
import { GitVersionDescriptor } from "Repos/Common/Contracts/Git";
import { getReposRoutingService } from "Repos/Common/ReposRoutingService";
import { getShortCommitId, refToBranchName } from "Repos/Common/Util/Ref";
import { branchToVersionDescriptor } from "Repos/Common/Util/Version";
import { getGitRepository } from "Repos/Common/VCViewModelDataProviderService";
import { IGitFilesService } from "Repos/Files/GitFilesService";
import * as Resources from "Repos/Views/PushDetails/Resources";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { PerformanceScenario, PerformanceScenarioContext } from "VSS/Platform/Components/Performance";
import { Components, useComponentContext } from "VSS/Platform/Layout";
import { tryGetLocalStorageValue, trySetLocalStorageValue } from "VSS/Platform/Util/Storage";
import { Ago } from "VSSUI/Ago";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { Spinner } from "VSSUI/Spinner";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IVssContributedTab, TabBar, TabContent } from "VSSUI/Tabs";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { getPushDetailsService } from "../PushDetailsService";

const hideGraphSettingsKey = "Repos.CommitsHub.HideGraph";

const PushDetailsView = (): JSX.Element => {
    const { pageContext } = useComponentContext();

    const filterVisible = new ObservableValue<boolean>(false);
    const showGraph = new ObservableValue(tryGetLocalStorageValue(hideGraphSettingsKey) !== "true");

    const compareTabCommandBarItems = new ObservableArray<IHeaderCommandBarItem>();

    const pushDetailsService = getPushDetailsService(pageContext);
    const selectedTabId = pushDetailsService.getSelectedTabId();

    const reposRoutingService = getReposRoutingService(pageContext);
    const repository = getGitRepository(pageContext)!;

    const pushDetailTabs = new ObservableArray<IVssContributedTab>([
        {
            id: "summary",
            name: Resources.Summary,
            render: () => <PushDetailsFilesTab />
        },
        {
            commandBarItems: compareTabCommandBarItems,
            id: "commits",
            name: Resources.Commits,
            render: () => {
                return (
                    <PerformanceScenarioContext.Consumer>
                        {scenarioProps => {
                            const pendingWork = scenarioProps.registerPendingWork("load-commits-table");
                            const baseVersion = pushDetailsService.getBaseVersion();

                            return (
                                <div className="page-content page-content-top flex-column flex-grow flex-noshrink rhythm-vertical-16">
                                    <CommitsHistoryView
                                        compareVersion={baseVersion ? pushDetailsService.getTargetVersion() : undefined}
                                        filterVisible={filterVisible}
                                        onLoaded={pendingWork!.complete}
                                        shouldUpdateUrl={true}
                                        showGraph={showGraph}
                                        version={baseVersion ? baseVersion : pushDetailsService.getTargetVersion()}
                                    />
                                </div>
                            );
                        }}
                    </PerformanceScenarioContext.Consumer>
                );
            }
        }
    ]);

    const updateTabHeaderItems = (): void => {
        const graphLabel = showGraph.value ? Resources.GraphShowingTooltip : Resources.GraphHiddenTooltip;
        compareTabCommandBarItems.value = [
            {
                ariaLabel: Resources.ShowFilter,
                iconProps: { iconName: filterVisible.value ? "FilterSolid" : "Filter" },
                id: "filter",
                onActivate: () => {
                    filterVisible.value = !filterVisible.value;
                    updateTabHeaderItems();
                },
                subtle: true
            },
            {
                ariaLabel: graphLabel,
                iconProps: { iconName: "GitGraph" },
                id: "showGraph",
                onActivate: () => {
                    trySetLocalStorageValue(hideGraphSettingsKey, showGraph.value ? "true" : undefined);
                    showGraph.value = !showGraph.value;
                    updateTabHeaderItems();
                },
                text: showGraph.value ? Resources.GraphOn : Resources.GraphOff,
                tooltipProps: { text: graphLabel }
            }
        ];
    };

    updateTabHeaderItems();

    return (
        <PerformanceScenario area="Repos" scenarioName="branch-compare" tti={true}>
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow custom-scrollbar scroll-auto-hide" selectedTabId={selectedTabId} tabProviders={pushDetailTabs}>
                    <Observer errorMessage={pushDetailsService.getErrorMessage()} ready={pushDetailsService.getReady()}>
                        {(props: { errorMessage: string | undefined; ready: boolean }) => {
                            if (props.errorMessage) {
                                return <MessageCard severity={MessageCardSeverity.Error}>{props.errorMessage}</MessageCard>;
                            }

                            if (!props.ready) {
                                return <Spinner className="flex-grow" />;
                            }
                            const refName = pushDetailsService.getRefName();
                            const baseVersion = pushDetailsService.getBaseVersion();
                            const targetVersion = pushDetailsService.getTargetVersion();

                            return (
                                <>
                                    <Header
                                        title={format(Resources.PushDetailsTitle, refToBranchName(refName), getShortCommitId(targetVersion.version))}
                                        titleSize={TitleSize.Large}
                                    >
                                        <div className="flex-row flex-grow padding-top-8 flex-center">
                                            <Persona
                                                className="flex-row flex-grow padding-right-8"
                                                identity={pushDetailsService.getPusher()}
                                                size={PersonaSize.size32}
                                            />
                                            <div className="flex-column flex-grow">
                                                <FormatComponent format={baseVersion ? Resources.UpdatedMessage : Resources.CreatedAt}>
                                                    <FPSLink
                                                        href={reposRoutingService.getFilesUrl(
                                                            repository.id,
                                                            repository.project.id,
                                                            undefined,
                                                            branchToVersionDescriptor(refToBranchName(refName))
                                                        )}
                                                    >
                                                        {refName}
                                                    </FPSLink>
                                                    {baseVersion && (
                                                        <FPSLink
                                                            href={reposRoutingService.getCommitUrl(
                                                                repository.id,
                                                                repository.project.id,
                                                                baseVersion.version
                                                            )}
                                                        >
                                                            {getShortCommitId(baseVersion.version)}
                                                        </FPSLink>
                                                    )}
                                                    <FPSLink
                                                        href={reposRoutingService.getCommitUrl(
                                                            repository.id,
                                                            repository.project.id,
                                                            targetVersion.version
                                                        )}
                                                    >
                                                        {getShortCommitId(targetVersion.version)}
                                                    </FPSLink>
                                                </FormatComponent>
                                                <span>
                                                    {format(Resources.PushedBy, pushDetailsService.getPusher().displayName)}
                                                    &nbsp;
                                                    <Ago date={pushDetailsService.getPushDate()} format={AgoFormat.Extended} />
                                                </span>
                                            </div>
                                        </div>
                                    </Header>
                                    <TabBar
                                        className="scroll-hidden flex-wrap"
                                        onSelectedTabChanged={(newTabId: string) => pushDetailsService.setSelectedTabId(newTabId)}
                                    />
                                    <TabContent />
                                </>
                            );
                        }}
                    </Observer>
                </Page>
            </Surface>
        </PerformanceScenario>
    );
};

const PushDetailsFilesTab = (): JSX.Element => {
    const { pageContext } = useComponentContext();
    const filesService = pageContext.getService<IGitFilesService>("IGitFilesService");
    const navigationService = getGitNavigationService(pageContext);
    const pushDetailsService = getPushDetailsService(pageContext);
    return (
        <Observer changes={pushDetailsService.getChanges()}>
            {(props: { changes: IChangeCollection<GitVersionDescriptor> | undefined }) => {
                if (!props.changes) {
                    return <Spinner className="flex-grow" />;
                }

                return (
                    <div className="flex-column flex-grow">
                        <ChangesContext.Provider value={{ filesService, navigationService }}>
                            <ChangesExplorerView addDiffKeyboardShortcuts={true} changes={props.changes} />
                        </ChangesContext.Provider>
                    </div>
                );
            }}
        </Observer>
    );
};

Components.add("pushDetailsView", PushDetailsView);
